import type { CSSProperties } from 'react';
import type { ThemeRegistry } from '@classy/theme-sdk';
import { useAuth } from './AuthContext.js';
import loginBg from '../../../../assets/auth/login-bg.png';
import styles from './LoginScreen.module.css';

export interface LoginScreenProps {
  /** Kept in the prop bag for API symmetry with the previous version
   *  — unused today now that the auth bg is fixed. */
  registry?: ThemeRegistry;
}

/**
 * Pre-auth landing. Renders the fixed auth-surface bg (no theme
 * rotation, no mascot) behind a liquid-glass sign-in card so the
 * pre-auth view feels deliberate. Once the teacher signs in, their
 * saved per-classroom theme takes over.
 */
export function LoginScreen(_props: LoginScreenProps) {
  const auth = useAuth();
  const isLoading = auth.status === 'loading';

  return (
    <div className={styles.screen} style={{ '--login-bg': `url(${loginBg})` } as CSSProperties}>
      <div className={styles.tint} aria-hidden="true" />
      <div className={styles.stack}>
        <section className={styles.card} aria-labelledby="login-title">
          <h1 id="login-title" className={styles.title}>
            Classy
          </h1>
          <p className={styles.tagline}>Morning meeting, made for the classroom.</p>
          <ul className={styles.bullets} aria-label="What classy does">
            <li>Calendar, weather &amp; today&apos;s question, one big screen</li>
            <li>Built for Promethean boards, projectors &amp; 4K kiosks</li>
          </ul>
          <button
            type="button"
            className={styles.signInButton}
            onClick={() => auth.signIn()}
            disabled={isLoading}
            data-testid="signin-button"
          >
            <MicrosoftLogo />
            <span>{isLoading ? 'Signing in…' : 'Sign in with Microsoft'}</span>
          </button>
          <p className={styles.fineprint}>
            Use your school Microsoft account. Your tenant must be approved before classrooms
            unlock.
          </p>
        </section>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg
      className={styles.logo}
      viewBox="0 0 23 23"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="10" height="10" x="1" y="1" fill="#F25022" />
      <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
      <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
      <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
    </svg>
  );
}
